/**
 * Chat @tag reference grammar — the LexIDE client-side PORT (workspace-file-tags
 * C3; analysis §7/§8). The agent references files by `@tag`; the chat applier
 * turns each registered `@tag` into a clickable <FileChip>.
 *
 * ⚠ SINGLE-SOURCE PARITY. The authoritative grammar lives in the runtime at
 * `lexruntime/openclaw/extensions/lexiun-core/src/display/translate.ts`
 * (frozen at [tags-M1], commit 4007ef98). The chat applier is a different package
 * and cannot import it at runtime, so `TAG_PROSE_RE` is PORTED byte-for-byte and
 * `filetag-grammar.test.ts` mirrors the runtime's boundary/email/#v cases. A
 * change here is a cross-package contract change — update BOTH copies together.
 *
 * The old boundary-tag grammar this file used to carry is DELETED (A7) — the agent
 * emits `@tag` only. The bare machine-name pass is KEPT as a safety net.
 *
 * Rules:
 *   - `@tag` matches only at a word boundary (a leading @ not preceded by a
 *     letter/digit/underscore/@), so `email@host`, `@@x`, and mid-word `x@y`
 *     never match. Body = a Unicode letter then letters/digits/underscore, 3–40.
 *   - an optional `#v<N>` version pin is captured (Stage B; carried, not yet used).
 *   - closed-world: the tokenizer only LOCATES `@tag`s; resolution (registered →
 *     chip, unknown → literal text) is the chip's job (graceful degradation §8).
 *   - `@tag` inside a code span/fenced block is NEVER matched (escape rule §6.4 +
 *     the evidence guard §6.3: quoted/verbatim regions must not chip-ify).
 */

/**
 * A `@tag` reference in prose — PORT of the runtime `TAG_PROSE_RE`
 * (`/(?<![\p{L}\p{N}_@])@(\p{L}[\p{L}\p{N}_]{2,39})(?:#v(\d+))?/gu`).
 * Group 1 = tag body (no `@`); group 2 = optional `#v` pin. Fixed-length
 * lookbehind (Chrome/Firefox always; Safari ≥16.4).
 */
export const TAG_PROSE_RE = /(?<![\p{L}\p{N}_@])@(\p{L}[\p{L}\p{N}_]{2,39})(?:#v(\d+))?/gu;

/**
 * The §1.5 machine-name safety net: a bare `<slug>__<doctype>__<yyyymmdd-hhmmss>.<ext>`
 * token the model emitted raw (optionally path-prefixed). Conservative — the
 * double-underscore + timestamp shape never fires on ordinary prose. Applied only
 * AFTER the @tag pass, so a machine name never surfaces to the lawyer as an OS name.
 */
export const MACHINE_NAME_RE =
  /(?:[\w.-]+\/)*[a-z0-9][a-z0-9-]*__[a-z0-9-]+__\d{8}-\d{6}\.[a-z0-9]{1,8}/g;

/**
 * Code regions whose contents are VERBATIM and never tokenized — a fenced block
 * (```…```) or an inline code span (`…`). Masking these first delivers the escape
 * rule (§6.4: `` `@tag` `` renders literally) and the primary evidence guard
 * (§6.3: an agent's quoted transcript, fenced, never chip-ifies its `@`-strings).
 */
export const CODE_REGION_RE = /```[\s\S]*?```|`[^`\n]*`/g;

/**
 * The stream-tip pattern: a trailing `@` optionally followed by a partial body (and
 * partial `#v`) at end-of-text, at a word boundary. While streaming this run is HELD
 * (not tokenized) so a tag still being typed doesn't flash as literal then re-render
 * as a chip. On the final (non-streaming) render it tokenizes normally.
 */
export const TAG_TIP_RE = /(?<![\p{L}\p{N}_@])@(?:\p{L}[\p{L}\p{N}_]*)?(?:#v?\d*)?$/u;

/** Fresh non-stateful clones of the `g`-flagged patterns (never share `.lastIndex`). */
export function tagProseRe(): RegExp {
  return new RegExp(TAG_PROSE_RE.source, "gu");
}
export function machineNameRe(): RegExp {
  return new RegExp(MACHINE_NAME_RE.source, "g");
}
export function codeRegionRe(): RegExp {
  return new RegExp(CODE_REGION_RE.source, "g");
}
